/**
 * Customers — list and detail, shared by salesperson and admin.
 *
 * The list leads with outstanding and days since last order, not with names.
 * A salesperson standing on a street needs to know which of these shops owes
 * money and which has gone quiet; the name is how they recognise the row, not
 * why they opened the screen.
 *
 * Credit terms are editable only with permission, and the endpoint enforces it
 * — the button is hidden for anyone else rather than failing on submit.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Page } from '../../components/Shell';
import {
  AuthImage,
  Banner, Button, Card, Chip, ChipRow, ConfirmSheet, Empty, ErrorState, FormSection, Money, MoneyInput,
  Select, Sheet, Skeleton, Switch, TextInput, Tile, useToast,
} from '../../components/ui';
import { MetricCard, Pill, GroupHead } from '../../components/Metric';
import { IconChevron, IconPhone, IconPin, IconChat, IconCalendar } from '../../components/Icons';
import { ReminderSheet } from '../../components/ReminderSheet';
import { CustomerRequirements } from '../../components/CustomerRequirements';
import { CustomerClassification } from '../../components/CustomerClassification';
import { ClassificationBadge, classificationNameStyle } from '../../components/ClassificationBadge';
import { ShopMiniMap } from '../../components/ShopMiniMap';
import { useQuery, useDebounced, useRefreshOnFocus } from '../../lib/useApi';
import { api, postFile, mediaUrl, openPdf, ApiError } from '../../lib/api';
import { getCoords, type CompressedImage } from '../../lib/camera';
import { CameraSheet } from '../../components/Camera';
import * as outbox from '../../lib/outbox';
import { useAuth } from '../../lib/auth';
import { useCart } from '../../lib/cart';
import { useT } from '../../lib/i18n';
import { money, dateShort, telLink, mapsLink } from '../../lib/format';
import { displayPhone, localDigits, phoneInputDigits } from '../../lib/phone';

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

interface CustomerListRow {
  id: string; customer_code: string; legal_name: string; trade_name: string | null;
  /** Behavioural tier (0074) — drives the badge and name colour. */
  classification: string | null;
  gstin: string | null; credit_limit_paise: number; discount_percent: number;
  abc_class: string | null; is_payment_risk: number; is_walk_in: number;
  /** '' when trading normally; otherwise BC's own 'Ship' | 'Invoice' | 'All'. */
  bc_blocked: string;
  status: string; phone: string; shop_count: number;
  /** The primary shop's store type, null when none is set. */
  store_type_name: string | null;
  /** The primary shop's beat/route, null when none is set. */
  beat_name: string | null;
  outstanding_paise: number;
  /** The overdue slice of outstanding — invoices past their due date. */
  overdue_paise: number;
  /** Credit the customer is in: unallocated receipts + invoice advances. */
  advance_paise: number;
  last_order_date: string | null;
  salesperson_name: string | null; salesperson_code: string | null;
}

/** The grouped KPI header, served whole-book by GET /api/customers/summary. */
interface CustomerSummary {
  groupA: {
    totalActive: number; totalCustomers: number;
    paymentRisk: number; dormant21d: number; syncErrors: number;
  };
  groupB: {
    outstandingPaise: number; outstandingDisplay: string; outstandingCount: number;
    overduePaise: number; overdueDisplay: string; overdueCount: number;
    creditExceeded: number;
    unallocatedAdvancePaise: number; unallocatedAdvanceDisplay: string; advanceCount: number;
  };
  groupC: {
    avgOrderFrequencyDays: number;
    avgOrderValuePaise: number; avgOrderValueDisplay: string;
    newThisMonth: number;
  };
}

type Filter = 'all' | 'outstanding' | 'advance' | 'overdue' | 'quiet' | 'risk';

/**
 * Admin-only list of soft-deleted customers, each restorable (item #7). Kept a
 * self-contained panel so the main paginated list is untouched.
 */
function DeletedCustomers() {
  const toast = useToast();
  const { data, loading, refetch } = useQuery<{ items: { id: string; legal_name: string; trade_name: string | null; customer_code: string }[] }>(
    '/api/customers?deleted=1&pageSize=200', { refreshOn: ['customers'] },
  );
  async function restore(id: string) {
    try { await api.post(`/api/customers/${id}/restore`, {}); toast.show('Customer restored.', 'ok'); void refetch(); }
    catch { toast.show('Could not restore.', 'danger'); }
  }
  const items = data?.items ?? [];
  return (
    <div className="stack-2">
      {loading && !data && <Skeleton rows={3} height={56} />}
      {data && items.length === 0 && <Empty title="No deleted customers" />}
      {items.map((r) => (
        <Card key={r.id}>
          <div className="row-between">
            <div className="grow" style={{ minWidth: 0 }}>
              <div className="t-semi">{r.trade_name || r.legal_name}</div>
              <div className="t-xs t-muted">{r.customer_code} · deleted</div>
            </div>
            <Button size="sm" onClick={() => void restore(r.id)}>Restore</Button>
          </div>
        </Card>
      ))}
    </div>
  );
}

export function CustomerList() {
  const t = useT();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const isAdminUser = user?.role === 'owner_admin';
  const [showDeleted, setShowDeleted] = useState(false);

  const [search, setSearch] = useState('');
  // The initial filter can arrive in the URL (?filter=outstanding), so a tile on
  // the dashboard opens this screen with the right chip already selected. Only
  // the known filters are honoured; anything else falls back to 'all'.
  const [searchParams] = useSearchParams();
  const urlFilter = searchParams.get('filter');
  const FILTERS: Filter[] = ['all', 'outstanding', 'advance', 'overdue', 'quiet', 'risk'];
  const [filter, setFilter] = useState<Filter>(
    FILTERS.includes(urlFilter as Filter) ? (urlFilter as Filter) : 'all',
  );
  const [adding, setAdding] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const query = useDebounced(search);

  /**
   * EVERY SALESPERSON SEES EVERY CUSTOMER.
   *
   * This used to default a salesperson's list to his own book —
   * `&salespersonId=...` unless he typed a search — on the reasoning that the
   * shops he is responsible for are the ones he opens the screen for.
   *
   * Two things were wrong with it. The client's rule, stated on 28 Sep, is that
   * any salesperson may sell to any shop; there is no book to be restricted to.
   * And the filter did not degrade gracefully: not one of the 374 customers
   * imported from Business Central has an owning salesperson, because BC's
   * Salesperson Code was never mapped onto one. So the filter matched nothing
   * and the screen said "No customers match" — which reads as an empty database
   * rather than as a filter, and is the first thing a new salesperson would see
   * on signing in.
   *
   * The server never enforced this. It accepts a salespersonId query parameter
   * and applies no role restriction of its own, so removing it here is the
   * whole change — there is no matching relaxation to make on the API, and a
   * salesperson could always have seen these customers by typing a search.
   */
  const mine = '';

  /**
   * THE LIST WAS ONE PAGE DEEP AND HAD NO WAY TO REACH THE SECOND.
   *
   * It asked for `pageSize=100` and rendered whatever came back. With 375
   * customers, 275 of them could not be reached by scrolling at all — only by
   * typing a search that happened to match. The tile said "first 100", which is
   * true and is not the same as telling somebody the other 275 exist.
   *
   * A salesperson looking for a shop he cannot remember the name of would
   * conclude it was not in the app.
   *
   * Pages now accumulate behind a Load more button. Not an infinite scroll: on
   * a phone, an infinite list makes the bottom of the screen unreachable, and
   * "how many are left" is exactly what somebody scanning a customer list wants
   * to know.
   */
  const [page, setPage] = useState(1);
  const [loaded, setLoaded] = useState<CustomerListRow[]>([]);

  const { data, loading, error, refetch } = useQuery<{
    items: CustomerListRow[]; total: number; hasMore: boolean;
  }>(
    `/api/customers?page=${page}&pageSize=100${query.trim() ? `&search=${encodeURIComponent(query.trim())}` : ''}${filter !== 'all' ? `&filter=${filter}` : ''}${mine}`,
    { deps: [query, page, filter], refreshOn: ['customers'] },
  );
  useRefreshOnFocus(refetch);

  // The grouped KPI header. Whole-book, independent of the paged list and of
  // the search box — the cards state the state of the entire ledger, not of
  // the hundred rows currently on screen. Its own refetch feeds the sync
  // banner's Retry.
  const summary = useQuery<CustomerSummary>('/api/customers/summary', {});
  useRefreshOnFocus(summary.refetch);

  // A new search starts again from the first page. Without this, typing a
  // search while on page 3 would ask the server for the third page of a result
  // set that may only have one, and the screen would go empty.
  // A new search OR a new filter starts the paged list again from the top — the
  // server now applies the filter across the whole book, so the accumulated
  // rows from the previous filter no longer belong.
  useEffect(() => { setPage(1); setLoaded([]); }, [query, filter]);

  useEffect(() => {
    if (!data) return;
    setLoaded((prev) => {
      const base = page === 1 ? [] : prev;
      /**
       * Merged by id rather than appended.
       *
       * `useRefreshOnFocus` re-runs the CURRENT page whenever the tab regains
       * focus, so a plain append would add the same hundred rows again every
       * time somebody switched away and back — a list that grows while nobody
       * is looking at it.
       */
      const seen = new Set(base.map((x) => x.id));
      return [...base, ...data.items.filter((x) => !seen.has(x.id))];
    });
  }, [data, page]);

  // The server already returned only the rows that match `filter` (whole-book,
  // not just this page), so the list is simply what has been loaded. The old
  // client-side filter here was the bug: it hid matches that had not been paged
  // in yet, so "Outstanding" showed one shop while the dashboard counted two.
  const rows = loaded;

  const allLoaded = !data?.hasMore;
  const s = summary.data;

  const addSheet = adding
    ? <AddShop onClose={() => setAdding(false)} onSaved={() => { setAdding(false); refetch(); summary.refetch(); }} />
    : null;

  return (
    <Page title={t('nav.customers')} wide>
      <div className="stack">
        {/*
          Header: the title is the Page's; here sits the primary action, moved
          to the top-right so it is reachable without scrolling past the whole
          book — the reason a salesperson opens this screen is often the shop
          they are standing in.

          Import / Export were asked for but are not wired: there is no CSV
          endpoint in the app, and a button that does nothing is worse than
          none. Flagged to the user rather than faked.
        */}
        <div className="row" style={{ justifyContent: 'space-between' }}>
          {isAdminUser ? (
            <Button variant="ghost" onClick={() => setShowDeleted((v) => !v)}>
              {showDeleted ? '← Active customers' : 'Deleted'}
            </Button>
          ) : <span />}
          <Button onClick={() => setAdding(true)}>+ Add customer</Button>
        </div>

        {showDeleted && isAdminUser ? (
          <DeletedCustomers />
        ) : (
        <>

        {/*
          Consolidated sync banner. One line for the whole book rather than a
          badge per row: the question is "is Business Central in step", and the
          answer is a single number. Retry re-pulls the counts; a dedicated
          "view details" screen does not exist yet, so it is not offered.
        */}
        {s && s.groupA.syncErrors > 0 && (
          <Banner
            tone="danger"
            title={`${s.groupA.syncErrors} customer ${s.groupA.syncErrors === 1 ? 'record' : 'records'} failed to sync`}
            action={(
              <Button
                size="sm"
                variant="ghost"
                busy={retrying}
                onClick={async () => {
                  setRetrying(true);
                  try {
                    const r = await api.post<{ requeued: number }>('/api/customers/sync/retry', {});
                    toast.show(
                      r.requeued > 0
                        ? `${r.requeued} record${r.requeued === 1 ? '' : 's'} sent back to Business Central. This clears in a minute or two.`
                        : 'Nothing to retry.',
                      'ok',
                    );
                    // Give the sync a moment, then re-read the count.
                    setTimeout(() => { summary.refetch(); refetch(); }, 4000);
                  } catch (err) {
                    toast.show(err instanceof Error ? err.message : 'Could not retry', 'danger');
                  } finally {
                    setRetrying(false);
                  }
                }}
              >
                Retry
              </Button>
            )}
          >
            <span className="t-sm">Changes to these shops have not reached Business Central.</span>
          </Banner>
        )}

        <TextInput
          placeholder={`${t('app.search')} — name, code or phone`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label={t('app.search')}
        />

        <ChipRow
          value={filter}
          onChange={setFilter}
          options={[
            // Counts are WHOLE-BOOK, from the summary — the same figures the
            // server filters on. Counting the loaded page while the server
            // filters the whole book is the bug that made this chip read "1"
            // next to a dashboard tile that counted two.
            { value: 'all', label: t('app.all'), count: s?.groupA.totalCustomers },
            {
              value: 'outstanding',
              label: t('admin.outstanding'),
              count: s?.groupB.outstandingCount,
            },
            {
              value: 'advance',
              label: 'Advance paid',
              count: s?.groupB.advanceCount,
            },
            { value: 'quiet', label: 'No order 21d+', count: s?.groupA.dormant21d },
            {
              value: 'risk',
              label: 'Payment risk',
              count: s?.groupA.paymentRisk,
            },
          ]}
        />

        {loading && !data && <Skeleton rows={5} />}
        {error && !data && <ErrorState message={error.message} onRetry={refetch} />}
        {data && rows.length === 0 && <Empty title="No customers match" />}

        <Card flush>
          <div className="list">
            {rows.map((row) => {
              const name = row.trade_name || row.legal_name;
              return (
                <div
                  key={row.id}
                  className="list__item list__item--tap"
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/customers/${row.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/customers/${row.id}`); }
                  }}
                >
                  <span className="grow" style={{ minWidth: 0, textAlign: 'left' }}>
                    <span className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                      {/*
                        `||`, NOT `??`. Business Central sends an empty STRING
                        for a customer with no second name, and `??` only falls
                        back on null — so this rendered 374 blank names.
                      */}
                      <span className="t-semi t-clip" style={classificationNameStyle(row.classification)}>{name}</span>
                      {row.classification && row.classification !== 'trusted' && (
                        <ClassificationBadge classification={row.classification} />
                      )}
                      {row.bc_blocked ? (
                        <Chip tone="danger">
                          {row.bc_blocked === 'All' ? 'Blocked'
                            : row.bc_blocked === 'Ship' ? 'No delivery'
                              : 'No invoice'}
                        </Chip>
                      ) : null}
                      {row.is_payment_risk === 1 && <Chip tone="danger">Risk</Chip>}
                      {row.status === 'pending_approval' && <Chip tone="warn">Pending</Chip>}
                    </span>
                    {/* CODE · CATEGORY · PHONE — the three a rep recognises a
                        shop by. A missing phone shows "no phone" via displayPhone. */}
                    <span className="t-xs t-muted" style={{ display: 'block' }}>
                      {row.customer_code}
                      {row.store_type_name ? ` · ${row.store_type_name}` : ''}
                      {' · '}
                      {displayPhone(row.phone)}
                    </span>
                  </span>
                  {/* On the Advance-paid list the row leads with the credit the
                      customer is in (green); everywhere else it leads with what
                      they owe (red). */}
                  {filter === 'advance'
                    ? (row.advance_paise > 0 && (
                        <Money paise={row.advance_paise} tone="ok" compact className="t-semi" />
                      ))
                    : (row.outstanding_paise > 0 && (
                        <Money paise={row.outstanding_paise} tone="danger" compact className="t-semi" />
                      ))}
                  <IconChevron width={18} height={18} style={{ color: 'var(--faint)' }} />
                </div>
              );
            })}
          </div>
        </Card>

        {/*
          How many are left, and a way to get them. The number is stated rather
          than left to a bare "Load more": the question somebody has when they
          cannot find a shop is "how much of the book am I looking at".
        */}
        {data && !allLoaded && (
          <Button
            block
            variant="ghost"
            busy={loading}
            onClick={() => setPage((p) => p + 1)}
          >
            {`Load more — ${Math.max(0, data.total - loaded.length)} remaining`}
          </Button>
        )}
        </>
        )}

        {addSheet}
      </div>
    </Page>
  );
}

// ---------------------------------------------------------------------------
// Detail
// ---------------------------------------------------------------------------

/** One Indian state, as the states master serves it. */
interface StateOption {
  /** Numeric GST state code — the first two digits of a GSTIN. */
  gstCode: string;
  name: string;
  /** Business Central's own State Code. Not interchangeable with gstCode. */
  bcCode: string;
}

interface Shop {
  id: string; shop_code: string; shop_name: string; is_primary: number;
  address_line1: string | null; address_line2: string | null; landmark: string | null;
  city: string | null; pincode: string | null;
  state_gst_code: string | null; store_type_id: string | null; beat_id: string | null;
  latitude: number | null; longitude: number | null;
  phone: string | null; area_name: string | null; store_type_name: string | null;
  beat_name: string | null; is_active: number;
  open_time: string | null; close_time: string | null; weekly_off_day: string | null;
}

interface CustomerDetail {
  customer: Record<string, unknown> & {
    id: string; customer_code: string; legal_name: string; trade_name: string | null;
    gstin: string | null; credit_limit_paise: number; discount_percent: number;
    abc_class: string | null; is_payment_risk: number; user_status: string;
    phone: string; salesperson_name: string | null; poc_name: string | null;
    alt_contact1_name: string | null; alt_contact1_phone: string | null;
    alt_contact2_name: string | null; alt_contact2_phone: string | null;
  };
  shops: (Shop & { mapsUrl: string | null; dialUrl: string | null })[];
  credit: {
    limitPaise: number; exposurePaise: number; availablePaise: number | null;
    utilisationPercent: number | null;
    display: { limit: string; exposure: string };
  };
  stats: {
    orderCount: number; lifetimeValuePaise: number;
    lastOrderDate: string | null; firstOrderDate: string | null;
    daysSinceLastOrder: number | null;
  };
  activity?: { last15Days: number; currentMonth: number; ytd: number };
  payments?: { upiPaise: number; cashPaise: number; upiDisplay: string; cashDisplay: string };
  creditStatus?: {
    status: 'active' | 'approaching' | 'overdue' | 'hold';
    overduePaise: number; overdueDisplay: string; blockScope: string;
  };
  consumption?: { totalPets: number; byCategory: { category: string; pets: number }[] };
}

export function CustomerDetail() {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const params = useParams();
  const { user, isStaff } = useAuth();
  const cart = useCart();
  const id = params.customerId ?? '';

  const { data, loading, error, refetch } = useQuery<CustomerDetail>(
    id ? `/api/customers/${id}` : null,
  );

  const [editing, setEditing] = useState(false);
  const [creditLimit, setCreditLimit] = useState<number | null>(null);
  const [discount, setDiscount] = useState('');
  const [risk, setRisk] = useState(false);
  const [busy, setBusy] = useState(false);

  /**
   * Blocking trade, which is Business Central's own four-value field rather
   * than a checkbox. See the block endpoint for why it is not flattened.
   */
  const [blockOpen, setBlockOpen] = useState(false);
  const [blockValue, setBlockValue] = useState<'' | 'Ship' | 'Invoice' | 'All'>('');
  const [blockReason, setBlockReason] = useState('');

  /**
   * The full customer card, editable — client request, 20 Sep.
   *
   * Everything Business Central shows on its customer card, in one place, and
   * everything here travels to BC on save. The fields live in three tables —
   * name and GSTIN on `customers`, phone on `users`, address and store type on
   * the primary shop — and that is the app's business, not the person's.
   */
  const [editOpen, setEditOpen] = useState(false);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [edit, setEdit] = useState({
    legalName: '', tradeName: '', pocName: '', phone: '', gstin: '',
    altContact1Name: '', altContact1Phone: '', altContact2Name: '', altContact2Phone: '',
    addressLine1: '', city: '', pincode: '', storeTypeId: '', beatId: '', stateGstCode: '',
  });

  // Only fetched when the sheet is open: 374 customer pages should not each
  // cost a master-data request nobody looks at.
  // Uses the OPEN store-types endpoint (any authenticated user), NOT the
  // admin-gated /masters/store-types — otherwise a salesman or driver editing a
  // shop got a 403, the Store-type picker never rendered, and the save still
  // demanded one ("Choose a store type before saving") with no way to pick it.
  const storeTypes = useQuery<{ items: { id: string; name: string }[] }>(
    editOpen ? '/api/admin/masters/store-types-for-customer' : null,
  );

  /**
   * The beats, for the picker below. Same lazy fetch as store types.
   *
   * Assignable from here because this is where somebody realises the shop is
   * on the wrong round. Until now the beat could only be set when a customer
   * was approved, so a shop that moved needed an admin to re-approve it.
   */
  const beats = useQuery<{ items: { id: string; code: string; name: string; area_name: string | null }[] }>(
    editOpen ? '/api/admin/masters/beats-for-customer' : null,
  );

  const states = useQuery<{ items: StateOption[]; defaultGstCode: string }>(
    editOpen ? '/api/admin/masters/states' : null,
  );

  // Only these roles can change commercial terms. The endpoint checks too;
  // hiding the button keeps a user from discovering the limit by being refused.
  const mayEditTerms = user?.role === 'owner_admin' || user?.role === 'manager';

  // Who may order on a retailer's behalf. Mirrors the server: the API grants
  // `order.create_on_behalf` to salesperson and driver, and exempts
  // owner_admin from the check. `manager` and `accountant` are excluded
  // because they hold no such grant and would be refused at submit — offering
  // the button would be a promise the server breaks.
  const mayOrderForCustomer = user?.role === 'owner_admin'
    || user?.role === 'salesperson'
    || user?.role === 'driver'
    || user?.role === 'field_agent';

  const startEdit = () => {
    if (!data) return;
    setCreditLimit(data.customer.credit_limit_paise);
    setDiscount(String(data.customer.discount_percent));
    setRisk(data.customer.is_payment_risk === 1);
    setEditing(true);
  };

  /**
   * Only owner_admin. `customer.edit` is granted to that role alone — a
   * manager holds `customer.approve` but not `customer.edit`, so offering the
   * button to managers would show them a control the server refuses.
   * The client's instruction on 18 Sep was "currently admin only" in any case.
   */
  const mayBlock = user?.role === 'owner_admin';

  // Admin deletes immediately (a soft-delete — history kept, hidden from search).
  // Field staff cannot delete; they RAISE a request an admin approves (item #7).
  const mayDelete = user?.role === 'owner_admin';
  const mayRequestDelete = !!user && ['salesperson', 'driver', 'field_agent'].includes(user.role);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const confirmDelete = async () => {
    setBusy(true);
    try {
      if (mayDelete) {
        await api.del(`/api/customers/${id}`);
        toast.show('Customer deleted. Their history is kept for reporting.', 'ok');
        setDeleteOpen(false);
        navigate('/customers');
      } else {
        await api.post(`/api/customers/${id}/delete-request`, {});
        toast.show('Deletion requested — an admin will review it.', 'ok');
        setDeleteOpen(false);
      }
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'Could not complete', 'danger');
    } finally {
      setBusy(false);
    }
  };

  const startBlock = () => {
    if (!data) return;
    const current = String(data.customer.bc_blocked ?? '');
    setBlockValue(current === 'Ship' || current === 'Invoice' || current === 'All' ? current : '');
    setBlockReason(String(data.customer.blocked_reason ?? ''));
    setBlockOpen(true);
  };

  const saveBlock = async () => {
    setBusy(true);
    try {
      const result = await api.post<{ changed: boolean; syncNote?: string; note?: string }>(
        `/api/customers/${id}/block`,
        { blocked: blockValue, reason: blockReason.trim() || undefined },
      );
      setBlockOpen(false);
      /**
       * The server's own sentence. It is the only one that knows whether
       * Business Central actually took the change, and "Saved" on its own was
       * true of the app and silent about the half the client asked for.
       */
      // Neutral when nothing changed — re-blocking an already blocked customer
      // is a no-op the server reports rather than refuses.
      toast.show(result.syncNote ?? result.note ?? 'Updated.', result.changed ? 'ok' : 'neutral');
      await refetch();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'Failed', 'danger');
    } finally {
      setBusy(false);
    }
  };

  const startEditDetails = () => {
    if (!data) return;
    const shop = data.shops.find((s) => s.is_primary === 1) ?? data.shops[0];
    setEdit({
      legalName: data.customer.legal_name ?? '',
      tradeName: data.customer.trade_name ?? '',
      pocName: data.customer.poc_name ?? '',
      altContact1Name: (data.customer as { alt_contact1_name?: string | null }).alt_contact1_name ?? '',
      altContact1Phone: localDigits((data.customer as { alt_contact1_phone?: string | null }).alt_contact1_phone ?? ''),
      altContact2Name: (data.customer as { alt_contact2_name?: string | null }).alt_contact2_name ?? '',
      altContact2Phone: localDigits((data.customer as { alt_contact2_phone?: string | null }).alt_contact2_phone ?? ''),
      /**
       * The last ten digits, or blank.
       *
       * A placeholder like "+91-BC-C00043" is not a phone number and is shown
       * as empty, so the field reads "not set" rather than inviting somebody
       * to edit a code. A real number is shown WITHOUT its +91, because that
       * is what the field asks for — displaying one format and expecting
       * another is how a good number gets mangled on an unrelated edit.
       */
      phone: localDigits(data.customer.phone),
      gstin: data.customer.gstin ?? '',
      addressLine1: shop?.address_line1 ?? '',
      city: shop?.city ?? '',
      pincode: shop?.pincode ?? '',
      storeTypeId: shop?.store_type_id ?? '',
      beatId: shop?.beat_id ?? '',
      /**
       * Pre-filled from the shop, NOT left blank to be defaulted.
       *
       * A blank state in an edit form is a trap: the person opens the sheet to
       * fix a pincode, the state shows as unset, and saving would either wipe
       * a correct state or silently replace it with Rajasthan. Neither is
       * something they asked for, and neither would be visible on the invoice
       * until the tax came out wrong.
       */
      stateGstCode: shop?.state_gst_code ?? '',
    });
    setEditOpen(true);
  };

  const saveDetails = async () => {
    // Beat and store type are mandatory on every customer edit — they drive the
    // delivery round and the reporting, and a shop with neither is a gap nobody
    // chases. Blocked here, and the fields are marked required on the sheet.
    if (!edit.beatId) { toast.show('Choose a beat before saving.', 'danger'); return; }
    if (!edit.storeTypeId) { toast.show('Choose a store type before saving.', 'danger'); return; }
    setBusy(true);
    try {
      /**
       * Only fields that have a value are sent.
       *
       * The endpoint treats every present key as an intentional change, so
       * sending an empty string would BLANK a name in Business Central because
       * somebody opened the sheet and closed it. Clearing a field deliberately
       * is a different act and is not offered here.
       */
      /**
       * EVERY FIELD IS SENT, EMPTY ONES INCLUDED.
       *
       * The sheet opens with the current values already in it, so a box left
       * empty is one somebody emptied deliberately — and the earlier version,
       * which skipped empty fields, made clearing anything impossible. You
       * could delete a wrong phone number, press save, be told it saved, and
       * find it still there.
       *
       * The legal name is the exception: a customer with no name is not a
       * correction, and Business Central would refuse it anyway.
       */
      const payload: Record<string, unknown> = {
        tradeName: edit.tradeName.trim(),
        pocName: edit.pocName.trim(),
        phone: edit.phone.trim(),
        // Two extra contacts — name + number each; empty clears the row.
        altContact1Name: edit.altContact1Name.trim() || null,
        altContact1Phone: edit.altContact1Phone.trim() || null,
        altContact2Name: edit.altContact2Name.trim() || null,
        altContact2Phone: edit.altContact2Phone.trim() || null,
        addressLine1: edit.addressLine1.trim(),
        city: edit.city.trim(),
        pincode: edit.pincode.trim(),
      };
      if (edit.legalName.trim()) payload.legalName = edit.legalName.trim();
      // GSTIN is nullable in the schema, so an empty box clears it — which
      // also flips the customer to a bill of supply, as it should.
      payload.gstin = edit.gstin.trim() ? edit.gstin.trim().toUpperCase() : null;
      if (edit.storeTypeId) payload.storeTypeId = edit.storeTypeId;
      /**
       * Always sent, empty included — like every other field on this sheet.
       *
       * The box opens holding the current beat, so an empty one is a beat
       * somebody deliberately removed. Skipping empties would make taking a
       * shop OFF a round impossible, which is the same defect the phone field
       * had: you could clear it, be told it saved, and find it still there.
       */
      payload.beatId = edit.beatId || null;
      if (edit.stateGstCode) payload.stateGstCode = edit.stateGstCode;

      // Optimistic concurrency (item #3): tell the server which version we loaded,
      // so a save over someone else's newer change is refused rather than silently
      // overwriting it. The server's "changed since you opened it" message is shown
      // by the catch below. Omitted when unknown, which keeps the old behaviour.
      const loadedAt = (data?.customer as { updated_at?: string | null } | undefined)?.updated_at;
      if (loadedAt) payload.expectedUpdatedAt = loadedAt;

      const result = await api.patch<{ syncNote?: string; syncedToBc?: boolean; syncPending?: boolean }>(
        `/api/customers/${id}`, payload,
      );
      setEditOpen(false);
      /**
       * The server's sentence, AND the server's verdict on whether it worked.
       *
       * The record is ALWAYS saved here — the patch succeeded. The only question
       * is Business Central, and there are three answers, three tones:
       *   - synced        → green, done.
       *   - queued        → neutral: BC was just unreachable, it will sync itself.
       *     This is NOT a failure, and a red banner here made every slow-BC save
       *     look like the edit was lost, so people saved again.
       *   - refused       → red: BC rejected it and a person must act (e.g. a beat
       *     whose dimension value is not in BC — see Settings → Sync).
       */
      toast.show(
        result.syncNote ?? 'Customer updated.',
        result.syncedToBc ? 'ok' : result.syncPending ? 'neutral' : 'danger',
      );
      await refetch();
    } catch (err) {
      /**
       * THE SERVER'S OWN SENTENCE, NOT THE GENERIC ONE.
       *
       * A validation failure arrives as `fields: { phone: "..." }` with a
       * summary message of "Please correct the highlighted fields". This sheet
       * has no per-field error display, so that summary was all anyone saw —
       * and the useful part, which names the shop or the staff member already
       * using that number, was thrown away at the last step.
       *
       * Refusing without saying why is barely better than failing.
       */
      const fields = (err as { fields?: Record<string, string> }).fields;
      const detail = fields ? Object.values(fields).join(' ') : null;
      toast.show(
        detail ?? (err instanceof Error ? err.message : 'Could not save'),
        'danger',
      );
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    const pct = Number(discount);
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
      toast.show('Discount must be between 0 and 100.', 'danger');
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/api/customers/${id}`, {
        creditLimitPaise: creditLimit ?? 0,
        discountPercent: pct,
        isPaymentRisk: risk,
      });
      setEditing(false);
      toast.show('Terms updated.', 'ok');
      await refetch();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'Failed', 'danger');
    } finally {
      setBusy(false);
    }
  };

  if (loading && !data) return <Page title={t('nav.customers')} back><Skeleton rows={4} /></Page>;
  if (error && !data) {
    return (
      <Page title={t('nav.customers')} back>
        <ErrorState message={error.message} onRetry={refetch} />
      </Page>
    );
  }
  if (!data) return <Page title={t('nav.customers')} back><Empty /></Page>;

  const { customer, shops, credit, stats } = data;
  // New detail metrics — optional so the page still renders if the API lags.
  const act = data.activity ?? { last15Days: 0, currentMonth: 0, ytd: 0 };
  const pay = data.payments
    ?? { upiPaise: 0, cashPaise: 0, upiDisplay: '₹0.00', cashDisplay: '₹0.00' };
  const cstatus = data.creditStatus
    ?? { status: 'active' as const, overduePaise: 0, overdueDisplay: '₹0.00', blockScope: '' };
  const cons = data.consumption
    ?? { totalPets: 0, byCategory: [] as { category: string; pets: number }[] };
  const statusLabel: Record<string, string> = {
    active: 'Active', approaching: 'Approaching limit', overdue: 'Overdue', hold: 'Credit hold',
  };
  const primary = shops.find((s) => s.is_primary === 1) ?? shops[0];
  /** '' when trading normally; otherwise Business Central's own enum value. */
  const blocked = String(customer.bc_blocked ?? '');

  return (
    <Page
      title={customer.trade_name ?? customer.legal_name}
      subtitle={customer.customer_code}
      back
      titleStyle={classificationNameStyle((customer as { classification?: string }).classification)}
      titleExtra={<ClassificationBadge classification={(customer as { classification?: string }).classification} />}
      actions={
        <div className="row" style={{ gap: 6 }}>
          {/* Name, contact, phone, GSTIN and address — everything BC shows on
              its customer card. Separate from Terms because credit limit and
              discount carry their own permissions and their own audit
              sensitivity. */}
          {/*
            ANY MEMBER OF STAFF, NOT JUST THE OWNER.
            
            This sheet edits DESCRIPTIVE facts — name, contact, phone, GSTIN,
            address, store type. The person best placed to correct them is
            whoever is standing in the shop, and until 3 Oct they could not:
            the button was gated to owner_admin alongside Block, so a
            salesperson who found a wrong address had no way to fix it.
            
            Terms stays owner_admin. Credit limit and discount decide money,
            and a salesperson editing his own customer's discount is
            self-service pricing. The server enforces that split too — this
            is not the only lock.
          */}
          {isStaff && (
            <Button size="sm" variant="ok" onClick={startEditDetails}>Edit</Button>
          )}
          {mayEditTerms && (
            <Button size="sm" variant="ghost" onClick={startEdit}>Terms</Button>
          )}
          {mayBlock && (
            <Button size="sm" variant="ghost" onClick={startBlock}>
              {blocked ? 'Unblock' : 'Block'}
            </Button>
          )}
          {(mayDelete || mayRequestDelete) && (
            <Button size="sm" variant="danger" onClick={() => setDeleteOpen(true)}>
              {mayDelete ? 'Delete' : 'Request deletion'}
            </Button>
          )}
        </div>
      }
    >
      <div className="stack">
        {/*
          FIRST, because it changes what every other control on this page
          means. A blocked retailer's "New order" button would raise an order
          Business Central will refuse at invoicing.
        */}
        {blocked && (
          <Banner
            tone="danger"
            title={
              blocked === 'All' ? 'Blocked — no trade at all'
                : blocked === 'Invoice' ? 'Blocked for invoicing'
                  : 'Blocked for shipping'
            }
          >
            <div className="stack-1" style={{ gap: 4 }}>
              <span className="t-sm">
                {blocked === 'All'
                  ? 'Business Central will refuse both deliveries and invoices for this customer.'
                  : blocked === 'Invoice'
                    ? 'Goods already dispatched can still be delivered, but no new invoice will post.'
                    : 'No new deliveries. Invoices for goods already sent will still post.'}
              </span>
              {customer.blocked_reason ? (
                <span className="t-xs">Reason: {String(customer.blocked_reason)}</span>
              ) : null}
              {/* Who decided, not just what was decided. The person who has to
                  answer the retailer is rarely the person who blocked them. */}
              {customer.blocked_at ? (
                <span className="t-xs t-muted">Blocked {String(customer.blocked_at)}</span>
              ) : null}
              <span className="t-xs t-muted">
                Their login still works — blocking trade and disabling an account are
                separate decisions.
              </span>
            </div>
          </Banner>
        )}

        {customer.user_status === 'pending_approval' && (
          <Banner tone="warn" title="Awaiting approval">
            <span className="t-sm">This customer cannot order until approved.</span>
          </Banner>
        )}
        {customer.is_payment_risk === 1 && (
          <Banner tone="danger" title="Flagged as a payment risk">
            <span className="t-sm">Take cash where possible.</span>
          </Banner>
        )}

        {/* Place an order for this retailer.
            Hidden while the customer is unapproved, because the order would
            be refused — and a button that fails is worse than no button.
            Hidden for a blocked customer for the same reason: whichever of
            BC's three blocks is set, the order cannot complete its journey. */}
        {mayOrderForCustomer && customer.user_status !== 'pending_approval' && !blocked && (
          <Button
            block
            size="lg"
            onClick={() => {
              cart.setCustomer(customer.id, customer.trade_name ?? customer.legal_name);
              if (primary) cart.setShop(primary.id);
              navigate('/catalogue');
            }}
          >
            New order for {customer.trade_name ?? customer.legal_name}
          </Button>
        )}

        {/* ===== Order activity ========================================= */}
        <div className="dash-group">
          <GroupHead title="Order activity" count={act.last15Days === 0 ? 1 : 0} />
          <div className="grid-auto">
            <MetricCard
              label="Last order"
              value={stats.lastOrderDate ? dateShort(stats.lastOrderDate) : 'Never'}
              tone={(stats.daysSinceLastOrder ?? 0) > 21 ? 'warn' : undefined}
              meta={stats.daysSinceLastOrder !== null ? `${stats.daysSinceLastOrder} days ago` : 'No orders yet'}
            />
            <MetricCard
              label="Orders · last 15 days"
              value={act.last15Days}
              tone={act.last15Days === 0 ? 'warn' : undefined}
              meta={act.last15Days === 0 ? 'Quiet — no recent orders' : undefined}
            />
            <MetricCard label="Orders · this month" value={act.currentMonth} meta="Current month" />
            <MetricCard label="Orders · this year" value={act.ytd} meta={`${stats.orderCount} lifetime`} />
          </div>
        </div>

        {/* ===== Financial & payments =================================== */}
        <div className="dash-group">
          <GroupHead
            title="Financial & payments"
            count={(cstatus.status === 'overdue' || cstatus.status === 'hold') ? 1 : 0}
          />
          <div className="grid-auto">
            <MetricCard
              label="Outstanding & credit"
              value={credit.display.exposure}
              tone={cstatus.status === 'overdue' || cstatus.status === 'hold' ? 'danger'
                : cstatus.status === 'approaching' ? 'warn' : undefined}
              meta={credit.limitPaise > 0 ? `of ${credit.display.limit} limit` : 'No credit limit set'}
            >
              <Pill tone={cstatus.status === 'active' ? 'ok'
                : cstatus.status === 'approaching' ? 'warn' : 'danger'}>
                {statusLabel[cstatus.status]}{cstatus.blockScope ? `: ${cstatus.blockScope}` : ''}
              </Pill>
              {cstatus.overduePaise > 0 && <Pill tone="danger">{cstatus.overdueDisplay} overdue</Pill>}
            </MetricCard>

            <MetricCard label="UPI payments" value={pay.upiDisplay} meta="Total collected" />
            <MetricCard label="Cash payments" value={pay.cashDisplay} meta="Total collected" />
            <MetricCard
              label="Lifetime value"
              value={money(stats.lifetimeValuePaise, { compact: true })}
              meta={`${stats.orderCount} orders`}
            />
          </div>
        </div>

        {/* ===== Category consumption (PET bottles) ===================== */}
        <div className="dash-group">
          <GroupHead title="Category consumption · PETs" />
          <div className="grid-auto">
            <MetricCard
              label="Total consumed"
              value={cons.totalPets}
              meta="PET bottles, all time"
            >
              {cons.totalPets === 0 && <Pill>No purchases yet</Pill>}
            </MetricCard>
            {cons.byCategory.slice(0, 6).map((c) => (
              <MetricCard key={c.category} label={c.category} value={c.pets} meta="PETs" />
            ))}
          </div>
        </div>

        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          {telLink(customer.phone) && (
            <a className="btn btn--ghost" href={telLink(customer.phone)!}>
              <IconPhone width={18} height={18} /> {t('app.call')}
            </a>
          )}
          <Button variant="ghost" onClick={() => navigate(`/chat/${customer.id}`)}>
            <IconChat width={18} height={18} /> {t('chat.title')}
          </Button>
          <Button variant="ghost" onClick={() => setReminderOpen(true)}>
            <IconCalendar width={18} height={18} /> Reminder
          </Button>
        </div>

        {reminderOpen && (
          <ReminderSheet
            open={reminderOpen}
            onClose={() => setReminderOpen(false)}
            customerId={customer.id}
            customerName={customer.trade_name || customer.legal_name}
            source="manual"
          />
        )}

        {/* The shop on a map, with its key facts — replaces the old Directions
            button. Shown to every role on the customer card. */}
        {primary && <ShopMiniMap shopId={primary.id} />}

        {/* Behavioural tier that governs selling (item #8). */}
        <CustomerClassification
          customerId={customer.id}
          current={String((customer as { classification?: string }).classification ?? 'trusted')}
          onChanged={refetch}
        />

        {/* What the shop has asked for beyond its orders (item #6). */}
        <CustomerRequirements customerId={customer.id} />

        <Card className="stack-2">
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <span className="t-sm t-muted">Legal name</span>
            <span className="t-semi">{customer.legal_name}</span>
          </div>
          {customer.poc_name && (
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span className="t-sm t-muted">Contact</span>
              <span className="t-semi">{customer.poc_name}</span>
            </div>
          )}
          {/* Two extra contacts (0076) — name + number, tappable to call. */}
          {([
            { name: customer.alt_contact1_name, phone: customer.alt_contact1_phone },
            { name: customer.alt_contact2_name, phone: customer.alt_contact2_phone },
          ] as { name?: string | null; phone?: string | null }[])
            .filter((x) => x.name || x.phone)
            .map((x, i) => (
              <div key={`xc${i}`} className="row" style={{ justifyContent: 'space-between' }}>
                <span className="t-sm t-muted">{x.name || 'Other contact'}</span>
                {x.phone && telLink(x.phone)
                  ? <a className="t-semi" href={telLink(x.phone)!}>{displayPhone(x.phone)}</a>
                  : <span className="t-semi">{displayPhone(x.phone ?? '')}</span>}
              </div>
            ))}
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <span className="t-sm t-muted">GSTIN</span>
            <span className="t-semi">{customer.gstin ?? 'Unregistered'}</span>
          </div>
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <span className="t-sm t-muted">Discount</span>
            <span className="t-semi">{customer.discount_percent}%</span>
          </div>
          {customer.salesperson_name && (
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span className="t-sm t-muted">Salesperson</span>
              <span className="t-semi">{customer.salesperson_name}</span>
            </div>
          )}
        </Card>

        <div className="t-semi">Shops ({shops.length})</div>
        {shops.map((shop) => (
          <Card key={shop.id} className="stack-2">
            <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
              <span className="t-semi">{shop.shop_name}</span>
              {shop.is_primary === 1 && <Chip outline>Primary</Chip>}
              {shop.is_active === 0 && <Chip tone="warn">Inactive</Chip>}
            </div>
            <div className="t-sm t-muted">
              {[shop.address_line1, shop.landmark, shop.city, shop.pincode]
                .filter(Boolean).join(', ')}
            </div>
            <div className="t-xs t-muted">
              {[shop.area_name, shop.beat_name, shop.store_type_name].filter(Boolean).join(' · ')}
              {shop.open_time && shop.close_time
                ? ` · ${shop.open_time}–${shop.close_time}`
                : ''}
              {shop.weekly_off_day ? ` · closed ${shop.weekly_off_day}` : ''}
            </div>
            <div className="row" style={{ gap: 8 }}>
              {shop.dialUrl && (
                <a className="btn btn--quiet btn--sm" href={shop.dialUrl}>{t('app.call')}</a>
              )}
              {shop.mapsUrl && (
                <a className="btn btn--quiet btn--sm" href={shop.mapsUrl} target="_blank" rel="noreferrer">
                  {t('app.directions')}
                </a>
              )}
              {!shop.mapsUrl && (
                <span className="t-xs t-muted">No location pinned</span>
              )}
            </div>
          </Card>
        ))}

        {/*
          Shop location and photo are NOT shown here — they are edited from the
          "Edit" (Customer details) sheet, so the detail page stays a clean
          read-only view.
        */}

        <Sheet
          open={editing}
          onClose={() => setEditing(false)}
          title="Commercial terms"
          footer={
            <>
              <Button variant="ghost" block onClick={() => setEditing(false)} disabled={busy}>
                {t('app.cancel')}
              </Button>
              <Button block busy={busy} onClick={() => void save()}>{t('app.edit')}</Button>
            </>
          }
        >
          <div className="stack-2">
            <MoneyInput
              label={t('admin.setCreditLimit')}
              valuePaise={creditLimit}
              onChangePaise={setCreditLimit}
              hint={credit.exposurePaise > 0
                ? `Currently owing ${credit.display.exposure}`
                : 'Zero means cash only'}
            />
            <TextInput
              label={t('admin.setDiscount')}
              numeric
              value={discount}
              onChange={(e) => setDiscount(e.target.value.replace(/[^\d.]/g, ''))}
            />
            <Switch
              checked={risk}
              onChange={setRisk}
              label="Flag as a payment risk"
              hint="Shows a warning to staff taking an order from this customer"
            />
            {creditLimit !== null && creditLimit < credit.exposurePaise && (
              <Banner tone="warn" title="New limit is below what is already owed">
                <span className="t-sm">
                  Existing invoices are unaffected, but new orders will need an override.
                </span>
              </Banner>
            )}
          </div>
        </Sheet>

        {/*
          THE CUSTOMER CARD, EDITABLE.

          The fields sit in three tables and that is deliberately invisible
          here. The client's request was to edit a customer the way you would
          in Business Central, where all of this is one card — making somebody
          visit three screens to correct one shop would be our table layout
          leaking into their day.

          Everything saved here travels to Business Central on the request,
          with the queue behind it if BC is unreachable.
        */}
        <Sheet
          open={editOpen}
          onClose={() => setEditOpen(false)}
          fullscreen
          title="Customer details"
          footer={
            <>
              <Button variant="ghost" block onClick={() => setEditOpen(false)} disabled={busy}>
                {t('app.cancel')}
              </Button>
              {/* Its own label, not the shared t('app.edit') — this submit saves the
                  whole customer card, so it reads "Update Customer Record" (asked
                  27 Oct). Changing t('app.edit') would rename every Edit button in
                  the app; this is scoped to the customer edit, shared by admin,
                  salesperson, driver and field_agent through this one screen. */}
              <Button block busy={busy} onClick={() => void saveDetails()}>Update Customer Record</Button>
            </>
          }
        >
          {/*
            ONE PAGE, GROUPED. The same three sections as the add form — who
            the customer is, where they are, how they are classified — so a
            person who has added a shop recognises the shape when they come
            back to edit one. The fields themselves were already complete
            here; this only reorders them under headings.
          */}
          <div className="stack">
            <FormSection title="Customer">
              <TextInput
                label="Legal name"
                required
                value={edit.legalName}
                onChange={(e) => setEdit({ ...edit, legalName: e.target.value })}
                hint="The name on the GST registration. This is what prints on the invoice."
              />
              <TextInput
                label="Trade name"
                value={edit.tradeName}
                onChange={(e) => setEdit({ ...edit, tradeName: e.target.value })}
                hint="What the customer is called, if different."
              />
              <TextInput
                label="Contact person"
                value={edit.pocName}
                onChange={(e) => setEdit({ ...edit, pocName: e.target.value })}
              />
              {/*
                TEN DIGITS. The +91 is the server's business, not the user's.

                `normalisePhone` already takes the last ten digits and prefixes
                the country code, so asking somebody to type +91 was the form
                demanding something the system already knew. Shown without it
                too — a field that displays "+919509318087" and expects
                "9509318087" back is a field that invites a wrong edit.
              */}
              <TextInput
                label="Mobile number"
                numeric
                placeholder="10 digits, or paste +91…"
                value={edit.phone}
                onChange={(e) => setEdit({ ...edit, phone: phoneInputDigits(e.target.value) })}
                hint="This is how the retailer signs in. One number signs in to one customer — the app refuses a number already used by another customer or by a staff member."
              />
              {/* Two more contacts — name + number each (e.g. Manager, Accounts).
                  The primary number above is the login; these are just for calling. */}
              <div className="t-xs t-muted" style={{ marginTop: 4 }}>Additional contacts (optional)</div>
              <div className="row" style={{ gap: 10, alignItems: 'flex-end' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <TextInput
                    label="Name" placeholder="e.g. Manager"
                    value={edit.altContact1Name}
                    onChange={(e) => setEdit({ ...edit, altContact1Name: e.target.value })}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <TextInput
                    label="Number" numeric placeholder="10 digits, or +91…"
                    value={edit.altContact1Phone}
                    onChange={(e) => setEdit({ ...edit, altContact1Phone: phoneInputDigits(e.target.value) })}
                  />
                </div>
              </div>
              <div className="row" style={{ gap: 10, alignItems: 'flex-end' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <TextInput
                    label="Name" placeholder="e.g. Accounts"
                    value={edit.altContact2Name}
                    onChange={(e) => setEdit({ ...edit, altContact2Name: e.target.value })}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <TextInput
                    label="Number" numeric placeholder="10 digits, or +91…"
                    value={edit.altContact2Phone}
                    onChange={(e) => setEdit({ ...edit, altContact2Phone: phoneInputDigits(e.target.value) })}
                  />
                </div>
              </div>
              <TextInput
                label="GSTIN"
                value={edit.gstin}
                onChange={(e) => setEdit({ ...edit, gstin: e.target.value.toUpperCase() })}
                hint="15 characters. Leave blank for an unregistered shop — that decides tax invoice versus bill of supply."
              />
            </FormSection>

            <FormSection title="Address">
              {/* Address and store type belong to the MAIN shop. A customer with
                  several outlets edits the others from the shops list below. */}
              <div className="t-xs t-muted">
                These belong to the main shop. A customer with several outlets edits
                the others from the shops list on the customer page.
              </div>
              <TextInput
                label="Address"
                value={edit.addressLine1}
                onChange={(e) => setEdit({ ...edit, addressLine1: e.target.value })}
              />
              <TextInput
                label="City"
                value={edit.city}
                onChange={(e) => setEdit({ ...edit, city: e.target.value })}
              />
              <TextInput
                label="Pincode"
                numeric
                maxLength={6}
                value={edit.pincode}
                onChange={(e) => setEdit({ ...edit, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) })}
              />
              {states.data && (
                <Select
                  label="State"
                  value={edit.stateGstCode}
                  onChange={(e) => setEdit({ ...edit, stateGstCode: e.target.value })}
                  options={[
                    { value: '', label: 'Leave unchanged' },
                    ...states.data.items.map((s) => ({
                      value: s.gstCode, label: `${s.name} · ${s.gstCode}`,
                    })),
                  ]}
                  hint="Decides CGST+SGST or IGST on every future invoice for this shop."
                />
              )}
            </FormSection>

            <FormSection title="Classification">
              {beats.data && (
                <Select
                  label="Beat"
                  required
                  value={edit.beatId}
                  onChange={(e) => setEdit({ ...edit, beatId: e.target.value })}
                  options={[
                    { value: '', label: 'Choose beat' },
                    ...beats.data.items.map((b) => ({
                      value: b.id,
                      label: b.area_name ? `${b.name} · ${b.area_name}` : b.name,
                    })),
                  ]}
                  hint="Which delivery round this shop is on."
                />
              )}

              {storeTypes.data && (
                <Select
                  label="Store type"
                  required
                  value={edit.storeTypeId}
                  onChange={(e) => setEdit({ ...edit, storeTypeId: e.target.value })}
                  options={[
                    { value: '', label: 'Choose store type' },
                    ...storeTypes.data.items.map((s) => ({ value: s.id, label: s.name })),
                  ]}
                />
              )}
            </FormSection>

            <FormSection title="Location & photo">
              {isStaff && primary && <ShopLocation shop={primary} onSaved={refetch} />}
              {isStaff && (
                <ShopPhoto
                  customerId={id}
                  r2Key={(customer as Record<string, unknown>).shop_image_r2_key as string | null}
                  onChanged={refetch}
                />
              )}
            </FormSection>

            <Banner tone="info">
              <span className="t-sm">
                Saved changes go to Business Central straight away. Credit limit and discount
                are on the Terms screen — they carry their own approvals.
              </span>
            </Banner>
          </div>
        </Sheet>

        {/*
          TRADE STATUS.

          Business Central's own four values, not a switch. 'Ship' and
          'Invoice' are genuinely different decisions — one stops goods going
          out, the other stops money being billed — and collapsing them into
          "blocked" would mean picking one for everybody.

          There is deliberately no delete beside this. Business Central refuses
          to delete a customer with ledger entries and GST requires the trail,
          so a delete button would be a promise neither system can keep.
        */}
        <Sheet
          open={blockOpen}
          onClose={() => setBlockOpen(false)}
          title="Trade status"
          footer={
            <>
              <Button variant="ghost" block onClick={() => setBlockOpen(false)} disabled={busy}>
                {t('app.cancel')}
              </Button>
              <Button
                block
                busy={busy}
                // A block without a reason is refused by the server. Disabling
                // here says so before the round trip rather than after it.
                disabled={blockValue !== '' && !blockReason.trim()}
                onClick={() => void saveBlock()}
              >
                {t('app.edit')}
              </Button>
            </>
          }
        >
          <div className="stack-2">
            <ChipRow
              value={blockValue}
              onChange={setBlockValue}
              options={[
                { value: '' as const, label: 'Trading normally' },
                { value: 'Ship' as const, label: 'No deliveries' },
                { value: 'Invoice' as const, label: 'No invoices' },
                { value: 'All' as const, label: 'Block everything' },
              ]}
            />

            <div className="t-xs t-muted">
              {blockValue === '' ? 'Orders, deliveries and invoices all proceed as usual.'
                : blockValue === 'Ship' ? 'Nothing new is dispatched. Goods already sent can still be invoiced.'
                  : blockValue === 'Invoice' ? 'Nothing new is invoiced. Deliveries already in progress continue.'
                    : 'Neither deliveries nor invoices are allowed. Existing history is untouched.'}
            </div>

            {blockValue !== '' && (
              <TextInput
                label="Reason"
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                hint="Required. The retailer will ask why, and whoever answers is rarely whoever decided."
              />
            )}

            <Banner tone="info" title="This is sent to Business Central">
              <span className="t-sm">
                The same block is set on the customer card there, so the two systems agree.
                It does not disable their login — they can still open the app and see what
                they owe.
              </span>
            </Banner>
          </div>
        </Sheet>

        {/* The statement. See the note on Statement itself for why this had
            to be added rather than built. */}
        <Statement customerId={id} />
      </div>

      <ConfirmSheet
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={() => void confirmDelete()}
        busy={busy}
        title={mayDelete ? 'Delete this customer?' : 'Request deletion?'}
        confirmLabel={mayDelete ? 'Delete customer' : 'Request deletion'}
        tone="danger"
        body={(
          <div className="stack-2">
            <p className="t-sm t-muted">
              {mayDelete
                ? `${customer.legal_name} will be removed from customer lists, search and new orders. Their orders, invoices and payment history are kept for reporting — this does not erase anything.`
                : `A request to delete ${customer.legal_name} will be sent to an admin. Nothing changes until the admin approves it.`}
            </p>
          </div>
        )}
      />
    </Page>
  );
}

// ---------------------------------------------------------------------------
// Statement of account
// ---------------------------------------------------------------------------

/**
 * A customer's running statement, on the customer's own page.
 *
 * WHY THIS IS A REACHABILITY FIX AND NOT A NEW REPORT
 *
 * GET /api/invoices/ledger/:customerId already existed and already did the
 * hard part — ageing buckets computed from live data, payments awaiting
 * verification kept OUT of the outstanding figure, crate liability tracked
 * separately because no deposit is charged. It was called by the customer's
 * own app, by the customer home screen and by the salesperson's collection
 * screen.
 *
 * By no admin screen at all. So the person who actually chases the money —
 * the accountant, the owner — could see an aggregate outstanding figure on
 * the Outstanding report and had no way to see where it came from for one
 * shop. Section 7.16 asks for a customer ledger, and the answer was that
 * three quarters of it had been built and pointed away from the people who
 * needed it.
 *
 * BUTTON-GATED on purpose. This is a second request with two 500-row queries
 * behind it, and most visits to a customer page are to check a phone number
 * or a credit limit. Loading it eagerly would tax every one of those.
 */
/**
 * One invoice, opened from the statement.
 *
 * Client, 7 Oct: "in statement of account: invoice details not visible."
 *
 * The statement listed numbers and amounts, and the rows were inert — so a
 * shopkeeper querying a line could not be answered without leaving the
 * screen, and for a salesperson there was nowhere else to go: the admin
 * invoices screen is behind a role guard he does not pass.
 *
 * The PDF is offered alongside rather than instead. It is the document the
 * shopkeeper recognises and the one to show him in a dispute, but it is a
 * file to wait for on a field connection, so the lines render first.
 */
function InvoiceLines({ id, onClose }: { id: string; onClose: () => void }) {
  const toast = useToast();

  interface Detail {
    invoice: {
      invoice_number: string; invoice_date: string; status: string;
      net_amount_paise: number; balance_amount_paise: number;
      taxable_amount_paise: number; cgst_amount_paise: number;
      sgst_amount_paise: number; igst_amount_paise: number;
    };
    items: {
      id: string; line_no: number; sku_code: string; product_name: string;
      uom: string; quantity: number; free_quantity: number;
      rate_paise: number; net_amount_paise: number;
    }[];
  }

  const { data, loading, error } = useQuery<Detail>(`/api/invoices/${id}`);
  const cancelled = data?.invoice.status === 'cancelled';

  return (
    <Sheet open title={data?.invoice.invoice_number ?? 'Invoice'} onClose={onClose}>
      {loading && <Skeleton rows={4} />}
      {error && <ErrorState message={error.message} />}
      {data && (
        <div className="stack">
          {cancelled && (
            <Banner tone="warn" title="This invoice was cancelled">
              <span className="t-sm">
                Nothing is owed on it. It is kept because its number was issued and
                has to be accounted for.
              </span>
            </Banner>
          )}

          <Card flush>
            <div className="list">
              {data.items.map((it) => (
                <div key={it.id} className="list__item list__item--static">
                  <div className="grow" style={{ minWidth: 0 }}>
                    <div className="t-sm t-clip">{it.product_name}</div>
                    <div className="t-xs t-muted">
                      {it.sku_code} · {it.quantity} {it.uom}
                      {it.free_quantity > 0 ? ` + ${it.free_quantity} free` : ''}
                      {' · '}<Money paise={it.rate_paise} compact />
                    </div>
                  </div>
                  <span className="t-sm t-mono">
                    <Money paise={it.net_amount_paise} compact />
                  </span>
                </div>
              ))}
            </div>
          </Card>

          <Card className="stack-2">
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span className="t-sm t-muted">Taxable</span>
              <span className="t-sm t-mono"><Money paise={data.invoice.taxable_amount_paise} /></span>
            </div>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span className="t-sm t-muted">GST</span>
              <span className="t-sm t-mono">
                <Money
                  paise={data.invoice.cgst_amount_paise + data.invoice.sgst_amount_paise
                    + data.invoice.igst_amount_paise}
                />
              </span>
            </div>
            <hr className="divider" />
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span className="t-sm t-semi">Total</span>
              <span className="t-sm t-mono t-semi"><Money paise={data.invoice.net_amount_paise} /></span>
            </div>
            {/* Never shown for a cancelled invoice — it owes nothing. */}
            {!cancelled && data.invoice.balance_amount_paise > 0 && (
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span className="t-sm t-warn">Still due</span>
                <span className="t-sm t-mono t-warn">
                  <Money paise={data.invoice.balance_amount_paise} />
                </span>
              </div>
            )}
          </Card>

          {/*
            openPdf, not an anchor. The PDF route needs the bearer token,
            which an <a href> cannot send — the mistake that returned 401 to
            every retailer who tapped the invoice link on the orders screen.
          */}
          <Button
            variant="ghost" block
            onClick={() => void openPdf(`/api/invoices/${id}/pdf`).catch(
              () => toast.show('Could not open the invoice PDF', 'danger'),
            )}
          >
            View the invoice PDF
          </Button>
        </div>
      )}
    </Sheet>
  );
}

function Statement({ customerId }: { customerId: string }) {
  const [open, setOpen] = useState(false);
  const [openInvoice, setOpenInvoice] = useState<string | null>(null);

  interface Ledger {
    outstandingPaise: number;
    outstandingDisplay: string;
    ageing: Record<string, number>;
    pendingVerification: { amountPaise: number; count: number; note: string };
    invoices: {
      id: string; invoice_number: string; invoice_date: string;
      net_amount_paise: number; balance_amount_paise: number;
      payment_status: string; status: string;
    }[];
    payments: {
      id: string; receipt_number: string; payment_date: string;
      amount_paise: number; payment_mode: string; verification_status: string;
    }[];
    returnablePackaging: { returnable_type: string; quantity_outstanding: number }[];
  }

  const { data, loading, error } = useQuery<Ledger>(
    open ? `/api/invoices/ledger/${customerId}` : null,
  );

  if (!open) {
    return (
      <Button variant="ghost" block onClick={() => setOpen(true)}>
        Statement of account
      </Button>
    );
  }

  if (loading && !data) return <Skeleton rows={3} />;
  if (error && !data) return <ErrorState message={error.message} />;
  if (!data) return <Empty title="No statement" />;

  const buckets = Object.entries(data.ageing);
  const crates = data.returnablePackaging.filter((r) => r.quantity_outstanding > 0);

  return (
    <div className="stack-4">
      <div className="grid-auto">
        <Tile
          label="Outstanding"
          value={data.outstandingDisplay}
          tone={data.outstandingPaise > 0 ? 'warn' : undefined}
        />
        {buckets.map(([label, paise]) => (
          <Tile
            key={label}
            label={`${label} days`}
            value={money(paise)}
            // 60+ is the bucket that decides whether to keep supplying.
            tone={label === '60+' && paise > 0 ? 'critical' : undefined}
          />
        ))}
      </div>

      {/* Money received but not yet approved. It has NOT reduced the
          outstanding figure above, and showing them together would mislead
          exactly the person deciding whether to release the next order. */}
      {data.pendingVerification.count > 0 && (
        <Banner tone="warn" title={`${money(data.pendingVerification.amountPaise)} awaiting approval`}>
          <span className="t-sm">{data.pendingVerification.note}</span>
        </Banner>
      )}

      {openInvoice && (
        <InvoiceLines id={openInvoice} onClose={() => setOpenInvoice(null)} />
      )}

      <Card flush>
        <div className="card__header"><h3>Invoices</h3></div>
        {data.invoices.length === 0 ? <Empty title="No invoices" /> : (
          <div className="list">
            {data.invoices.slice(0, 25).map((i) => (
              /**
               * TAPPABLE. Reported 7 Oct as "invoice details not visible".
               *
               * The rows were `list__item--static` — a list of numbers and
               * amounts with no way to see what was actually sold. A
               * shopkeeper querying a line had to be told to wait.
               */
              <button
                key={i.id}
                type="button"
                className="list__item list__item--tap"
                onClick={() => setOpenInvoice(i.id)}
              >
                <div className="grow" style={{ minWidth: 0 }}>
                  <div className="t-sm t-semi t-clip">{i.invoice_number}</div>
                  <div className="t-xs t-muted">
                    {dateShort(i.invoice_date)}
                    {i.status === 'cancelled' && ' · cancelled'}
                  </div>
                </div>
                <div className="t-right">
                  <div className="t-sm t-mono"><Money paise={i.net_amount_paise} compact /></div>
                  {/*
                    A CANCELLED INVOICE IS NEVER DUE.

                    Reported as "if order is cancelled then why due amount is
                    being displayed". The server stopped stranding a balance
                    on cancellation on 7 Oct and the existing rows were
                    repaired, so this guard is belt and braces — but it is
                    the one the client actually sees, and it costs nothing.
                  */}
                  {i.status !== 'cancelled' && i.balance_amount_paise > 0 && (
                    <div className="t-xs t-warn">
                      due <Money paise={i.balance_amount_paise} compact />
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </Card>

      <Card flush>
        <div className="card__header"><h3>Payments</h3></div>
        {data.payments.length === 0 ? <Empty title="No payments" /> : (
          <div className="list">
            {data.payments.slice(0, 25).map((p) => (
              <div key={p.id} className="list__item list__item--static">
                <div className="grow" style={{ minWidth: 0 }}>
                  <div className="t-sm t-semi t-clip">{p.receipt_number}</div>
                  <div className="t-xs t-muted">
                    {dateShort(p.payment_date)} · {p.payment_mode}
                  </div>
                </div>
                <div className="row" style={{ gap: 8 }}>
                  {p.verification_status !== 'verified' && (
                    <Chip tone="warn">{p.verification_status}</Chip>
                  )}
                  <span className="t-sm t-mono"><Money paise={p.amount_paise} compact /></span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Crates are a liability without being a debt: no deposit is charged,
          so they are deliberately outside the outstanding figure. */}
      {crates.length > 0 && (
        <Card>
          <div className="card__header"><h3>Returnable packaging</h3></div>
          {crates.map((r) => (
            <div key={r.returnable_type} className="row">
              <span className="grow t-sm">{r.returnable_type.replace(/_/g, ' ')}</span>
              <span className="t-semi t-mono">{r.quantity_outstanding}</span>
            </div>
          ))}
          <div className="t-xs t-muted" style={{ marginTop: 6 }}>
            Not part of the outstanding balance — no deposit is charged.
          </div>
        </Card>
      )}

      <Button variant="quiet" block onClick={() => setOpen(false)}>Hide statement</Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Registering a shop from the field
//
// WHY THIS EXISTS
//
// POST /api/customers/register has always supported a staff-registered
// customer: it accepts `isWalkIn`, records `registration_source` as
// salesperson or driver, and links the salesperson by code. It was called
// from exactly ONE place — the retailer's own self-registration screen.
//
// So a salesperson standing in a shop could not onboard it. The single most
// common acquisition moment in this trade had to be phoned in to the office,
// and the walk-in path — a cash sale to an unregistered shop — could not be
// invoiced at all.
//
// TWO THINGS THIS SCREEN TAKES SERIOUSLY
//
// 1. CONSENT COLLECTED BY A THIRD PARTY. When a salesperson registers a shop,
//    the shopkeeper is the data subject and the salesperson is typing on their
//    behalf. A tickbox that says "agreed" without anyone having read the terms
//    aloud is worthless, and worse than worthless if it is later produced as
//    evidence. So the wording is explicit about what the staff member is
//    attesting to, and `registration_source` records that it was staff-
//    collected rather than given first-hand.
//
// 2. THE GPS PIN FOLLOWS THE CONSENT. If location capture is declined, no
//    coordinates are sent — not captured and discarded, not captured and
//    ignored. Asking permission and then taking the pin anyway is how consent
//    becomes theatre.
// ---------------------------------------------------------------------------

/**
 * Where the shop actually is.
 *
 * Three ways in, because the location arrives three ways in this trade and
 * refusing two of them means the field stays empty:
 *
 *   1. Standing outside it — the device's own GPS fix. The best one, and the
 *      only one that carries an accuracy figure.
 *   2. A Google Maps link, pasted. This is how a shopkeeper sends his own
 *      location over WhatsApp, and how somebody at the office finds a shop
 *      that was onboarded over the phone.
 *   3. Typed coordinates, for the rare case where somebody has them.
 *
 * The SOURCE is saved alongside, and shown, because a pin dropped from a desk
 * fifty kilometres away and a fix taken at the door look identical as numbers
 * and mean very different things to a driver who cannot find the shop.
 */
function ShopLocation({ shop, onSaved }: {
  shop: Shop & { mapsUrl: string | null };
  onSaved: () => void;
}) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState('');
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');

  const source = (shop as unknown as { location_source?: string | null }).location_source ?? null;
  const has = shop.latitude != null && shop.longitude != null;

  const save = async (payload: Record<string, unknown>) => {
    setBusy(true);
    try {
      await api.patch(`/api/customers/shops/${shop.id}/location`, payload);
      toast.show('Location saved', 'ok');
      setOpen(false);
      setLink(''); setLat(''); setLng('');
      onSaved();
    } catch (err) {
      // The server's sentence, not ours. It is the one that knows WHY a link
      // had no coordinates in it, and "could not save" would send somebody to
      // try the same short link again.
      toast.show(err instanceof ApiError ? err.message : 'Could not save the location', 'danger');
    } finally {
      setBusy(false);
    }
  };

  const captureHere = async () => {
    setBusy(true);
    const coords = await getCoords({ highAccuracy: true });
    setBusy(false);
    if (!coords) {
      toast.show('No GPS fix. Step outside, or paste a Google Maps link instead.', 'danger');
      return;
    }
    await save({
      latitude: coords.latitude,
      longitude: coords.longitude,
      accuracyM: coords.accuracyM,
      source: 'gps',
    });
  };

  return (
    <>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {has && (
          <span className="t-xs t-muted">
            {shop.latitude!.toFixed(5)}, {shop.longitude!.toFixed(5)}
            {source === 'gps' && ' · taken at the shop'}
            {source === 'maps_link' && ' · from a Maps link'}
            {source === 'manual' && ' · entered by hand'}
          </span>
        )}
        <Button size="sm" variant="quiet" busy={busy} onClick={() => void captureHere()}>
          {has ? 'Update from here' : 'Capture location'}
        </Button>
        <Button size="sm" variant="quiet" onClick={() => setOpen(true)}>
          Paste Google link
        </Button>
      </div>

      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        title={`Location — ${shop.shop_name}`}
        footer={
          <>
            <Button variant="ghost" block onClick={() => setOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button
              block busy={busy}
              disabled={!link.trim() && !(lat.trim() && lng.trim())}
              onClick={() => void (link.trim()
                ? save({ locationUrl: link.trim(), source: 'maps_link' })
                : save({
                  latitude: Number(lat), longitude: Number(lng), source: 'manual',
                }))}
            >
              Save
            </Button>
          </>
        }
      >
        <div className="stack-3">
          <TextInput
            label="Google link"
            placeholder="https://maps.app.goo.gl/…"
            value={link}
            onChange={(e) => setLink(e.target.value)}
            hint={'Paste the share link from the Google Maps app, or the full map URL — '
              + "we'll pull the exact coordinates automatically."}
          />
          <div className="t-xs t-muted">or type them</div>
          <div className="row" style={{ gap: 8 }}>
            <TextInput
              label="Latitude" numeric value={lat} placeholder="26.912400"
              onChange={(e) => setLat(e.target.value.replace(/[^\d.-]/g, ''))}
            />
            <TextInput
              label="Longitude" numeric value={lng} placeholder="75.787300"
              onChange={(e) => setLng(e.target.value.replace(/[^\d.-]/g, ''))}
            />
          </div>
          {has && (
            <Button
              variant="danger" block busy={busy}
              onClick={() => void save({ latitude: null, longitude: null, source: 'manual' })}
            >
              Clear the location
            </Button>
          )}
        </div>
      </Sheet>
    </>
  );
}

/**
 * A photograph of the shop front.
 *
 * Taken with the CAMERA, not chosen from the gallery — `capturePhoto` sets
 * `capture="environment"`, so this is a picture of the place the person is
 * standing in, which is the only version of this field worth having.
 *
 * It syncs to the customer's picture in Business Central when the GDS customer
 * API page publishes one. It does not today, and the Business Central screen
 * says so rather than this one pretending the push worked.
 */
function ShopPhoto({ customerId, r2Key, onChanged }: {
  customerId: string;
  r2Key: string | null;
  onChanged: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);

  const take = async (photo: CompressedImage) => {
    setBusy(true);
    try {
      await postFile(`/api/customers/${customerId}/photo`, photo.blob, 'shop.jpg');
      toast.show('Shop photo saved', 'ok');
      onChanged();
    } catch (err) {
      toast.show(err instanceof ApiError ? err.message : 'Could not save the photo', 'danger');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.del(`/api/customers/${customerId}/photo`);
      toast.show('Shop photo removed', 'ok');
      onChanged();
    } catch (err) {
      toast.show(err instanceof ApiError ? err.message : 'Could not remove the photo', 'danger');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="stack-2">
      <span className="t-semi">Shop photo</span>
      {/*
        AuthImage: `shop_photo` is not a public asset.

        Only `product_image` and `upi_qr` are served without a token; every
        other purpose is checked against who is asking. An `<img src>` cannot
        send the bearer token, so this got a 401 and showed its alt text —
        indistinguishable from "no photo yet", which is the message right
        beside it.
      */}
      {r2Key ? (
        <AuthImage
          path={r2Key}
          alt="Shop front"
          style={{ width: '100%', maxHeight: 220, objectFit: 'cover', borderRadius: 8 }}
        />
      ) : (
        <span className="t-xs t-muted">No photo yet.</span>
      )}
      {/* The shop front, photographed where the person is standing. */}
      <CameraSheet
        open={cameraOpen}
        facing="environment"
        title="Photograph the shop front"
        compressOptions={{ maxDimension: 1600, maxBytes: 3 * 1024 * 1024 }}
        onCapture={(img) => void take(img)}
        onClose={() => setCameraOpen(false)}
      />
      <div className="row" style={{ gap: 8 }}>
        <Button size="sm" variant="quiet" busy={busy} onClick={() => setCameraOpen(true)}>
          {r2Key ? 'Retake' : 'Take photo'}
        </Button>
        {r2Key && (
          <Button size="sm" variant="quiet" busy={busy} onClick={() => void remove()}>
            Remove
          </Button>
        )}
      </div>
    </Card>
  );
}

function AddShop({
  onClose, onSaved,
}: {
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useT();
  const toast = useToast();
  /**
   * Attribution is the server's job — see the note in buildPayload — but the
   * ROLE is still needed here, for one reason: what this button promises.
   *
   * The server creates an owner_admin's customer as `active` and pushes it to
   * Business Central on the spot. Everyone else's is `pending_approval` and
   * reaches BC only when somebody approves it. The button said "Send for
   * approval" to both, and the note underneath told an admin their customer
   * would go to the office for approval — an approval that, for them, does not
   * exist and never arrives.
   *
   * So the admin waits for a queue entry that was never created, and the thing
   * the app told them to expect is the one thing that cannot happen. A label
   * that describes a different user's workflow is not a small inaccuracy; it is
   * the app lying about what the button did.
   */
  const { user } = useAuth();
  const approvesOwnEntry = user?.role === 'owner_admin';
  // For jumping straight to a shop this number already belongs to.
  const navigate = useNavigate();

  const [walkIn, setWalkIn] = useState(false);
  const [legalName, setLegalName] = useState('');
  const [shopName, setShopName] = useState('');
  const [phone, setPhone] = useState('');
  const [address1, setAddress1] = useState('');
  /**
   * City and pincode — added so the add form captures the same address the
   * edit form does. Both were already accepted by the register endpoint
   * (shopSchema.city, shopSchema.pincode) and simply never offered here, so a
   * shop added in the field reached Business Central with no pincode until
   * somebody reopened it to type one. Pincode is validated to six digits, the
   * same rule the server applies.
   */
  const [city, setCity] = useState('');
  const [pincode, setPincode] = useState('');
  const [stateGstCode, setStateGstCode] = useState('');

  /**
   * The states, and the company's own state as the default.
   *
   * The default comes from the server rather than being written '08' here: the
   * app already holds that value in one place, and a second copy in the web
   * bundle is one that can disagree with the first.
   */
  const states = useQuery<{ items: StateOption[]; defaultGstCode: string }>(
    '/api/admin/masters/states',
  );
  useEffect(() => {
    if (!stateGstCode && states.data?.defaultGstCode) {
      setStateGstCode(states.data.defaultGstCode);
    }
  }, [states.data, stateGstCode]);
  const [gstin, setGstin] = useState('');
  const [coords, setCoords] = useState<{ latitude: number; longitude: number; accuracyM: number } | null>(null);
  const [locating, setLocating] = useState(false);

  /**
   * The three fields the client asked for on 7 Oct.
   *
   * None of them needed any server work: `pocName`, `storeTypeId` and
   * `beatId` were already accepted by the register endpoint, already written
   * to columns that have existed since migration 0002, and already pushed to
   * Business Central — the contact as `contact`, the store type as Global
   * Dimension 1 and the beat as Global Dimension 2.
   *
   * They were simply never on this form, while being on the EDIT sheet. So
   * the flow was: add the shop, then immediately reopen it to fill in the
   * things nobody asked for. A shop registered and never reopened reached BC
   * with no dimensions at all, which makes its sales unattributable in BC's
   * own reporting.
   */
  const [pocName, setPocName] = useState('');
  const [storeTypeId, setStoreTypeId] = useState('');
  const [beatId, setBeatId] = useState('');

  /**
   * Both pickers are served by deliberately unguarded endpoints. The guarded
   * `/masters/:type` needs `admin.manage_masters`, which neither a
   * salesperson nor a driver holds — behind it these would render empty, and
   * an empty picker is a control that looks broken.
   */
  const storeTypes = useQuery<{ items: { id: string; code: string; name: string }[] }>(
    '/api/admin/masters/store-types-for-customer',
  );
  const beats = useQuery<{ items: { id: string; code: string; name: string; area_name: string | null }[] }>(
    '/api/admin/masters/beats-for-customer',
  );

  /** The shop front, captured at the door. Uploaded after the customer exists. */
  const [photo, setPhoto] = useState<CompressedImage | null>(null);
  const [camera, setCamera] = useState(false);

  /**
   * A preview URL, revoked when it is replaced.
   *
   * `CompressedImage` carries a Blob and no data URL, so one is minted here.
   * Without the revoke each retake leaks the previous image for the lifetime
   * of the tab — which on a cheap field phone, over a day of registrations,
   * is the kind of thing that ends as "the app gets slow".
   */
  const photoUrl = useMemo(() => (photo ? URL.createObjectURL(photo.blob) : null), [photo]);
  useEffect(() => () => { if (photoUrl) URL.revokeObjectURL(photoUrl); }, [photoUrl]);

  /**
   * The shop this number already belongs to.
   *
   * Set from the server's CUSTOMER_EXISTS reply, which carries the id so this
   * sheet can offer to open it rather than leaving the salesman to back out
   * and search for it by hand, in a shop, with the shopkeeper waiting.
   */
  const [existing, setExisting] = useState<
    { customerId: string; customerCode: string; legalName: string; shopName: string;
      awaitingApproval: boolean; message: string } | null
  >(null);

  // Consent, collected on the shopkeeper's behalf. Default off — a
  // pre-ticked attestation is not an attestation.
  //
  // The separate location/photo toggles were removed: the location and photo
  // cards are now always shown, matching the customer page, and this single
  // attestation covers them. The pin and photo are still sent only if actually
  // captured, so nothing is collected without the person having agreed.
  const [readAloud, setReadAloud] = useState(false);
  /** A pasted Google Maps link, used when there is no GPS fix. */
  const [locationLink, setLocationLink] = useState('');
  const [showLink, setShowLink] = useState(false);

  const [busy, setBusy] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const digits = phone.replace(/\D/g, '');

  /**
   * THE NUMBER IS OPTIONAL — but it is either absent or right.
   *
   * Client, 7 Oct. A driver at the door whose shopkeeper will not give a
   * number still has to be able to sell, and the previous rule stopped him
   * dead: the save button simply stayed grey, with nothing saying why.
   *
   * A HALF-TYPED number is still refused, because that is a mistake rather
   * than a decision. Blank means "not given"; four digits means "I was
   * interrupted", and saving that would store a number that reaches nobody
   * while looking exactly like one that does.
   *
   * What the shop loses without a number is written on the form rather than
   * discovered later: no login, no WhatsApp invoice, no payment reminder.
   */
  const phoneOk = digits.length === 0 || digits.length === 10;
  /**
   * Blank, or a complete six digits — never a half-typed pincode. Same shape
   * as the phone rule above and for the same reason: an absent pincode is a
   * decision, three digits is an interruption, and the server rejects the
   * latter with a Zod error rather than storing it.
   */
  const pincodeOk = pincode.length === 0 || pincode.length === 6;
  const valid = readAloud
    && shopName.trim().length >= 2
    && phoneOk
    && pincodeOk
    // Store type and beat are mandatory for a full customer (client, 24 Oct) —
    // a walk-in is the only exemption, being a minimal one-time record. The
    // server enforces the same rule; this just stops the Save before the trip.
    && (walkIn || (legalName.trim().length >= 2 && address1.trim().length >= 3
                   && !!storeTypeId && !!beatId));

  async function captureLocation() {
    setLocating(true);
    const c = await getCoords({ highAccuracy: true });
    setLocating(false);
    if (!c) {
      toast.show('Could not get a location. You can still save the shop.', 'danger');
      return;
    }
    setCoords(c);
  }

  function buildPayload() {
    return {
      legalName: walkIn ? shopName.trim() : legalName.trim(),
      tradeName: walkIn ? undefined : shopName.trim(),
      // Omitted rather than sent empty when not given: the server treats an
      // absent number as a deliberate absence and stores a stand-in.
      phone: digits || undefined,
      pocName: pocName.trim() || undefined,
      gstin: gstin.trim().toUpperCase() || undefined,
      isWalkIn: walkIn || undefined,
      /**
       * No salespersonCode. The SERVER attributes it.
       *
       * My first version sent `user.salespersonCode`, which does not exist on
       * the auth object — only salespersonId does. The route already handles
       * this correctly: an explicit code is validated against the
       * salespersons table so a typo cannot orphan a customer, and when none
       * is given and the caller IS a salesperson it uses their own id.
       *
       * Sending a code from here would therefore be strictly worse: the same
       * result on the happy path, and an invented value to go wrong on every
       * other. A driver registering a shop leaves it unattributed, which is
       * correct — the driver is not its salesperson.
       */
      shops: [{
        shopName: shopName.trim(),
        addressLine1: walkIn ? (address1.trim() || 'Walk-in — address not captured') : address1.trim(),
        // Omitted when empty rather than sent blank, matching every optional
        // shop field here. Pincode is only sent once it is a full six digits
        // (guarded by `pincodeOk` on the save button).
        ...(city.trim() ? { city: city.trim() } : {}),
        ...(pincode.length === 6 ? { pincode } : {}),
        /**
         * Sent even when it equals the default, so the shop RECORDS its state
         * rather than inheriting one. A stored '08' and an absent '08' behave
         * identically today and mean completely different things the moment
         * somebody asks why a retailer was billed CGST.
         */
        ...(stateGstCode ? { stateGstCode } : {}),
        /**
         * Location: coordinates win, a pasted Maps link is the fallback for
         * the server to parse. Sent only when actually captured, so a shop
         * with neither is created without one rather than with an empty pin.
         */
        latitude: coords?.latitude,
        longitude: coords?.longitude,
        locationAccuracyM: coords?.accuracyM,
        ...(coords ? { locationSource: 'gps' as const } : {}),
        ...(!coords && locationLink.trim()
          ? { locationUrl: locationLink.trim(), locationSource: 'maps_link' as const }
          : {}),
        storeTypeId: storeTypeId || undefined,
        beatId: beatId || undefined,
        isPrimary: true,
      }],
      consents: {
        termsOfService: readAloud,
        privacyPolicy: readAloud,
        dataProcessing: readAloud,
        whatsappCommunication: readAloud,
        // Recorded for what was actually collected, under the single
        // attestation above.
        locationCapture: !!coords || !!locationLink.trim(),
        photoCapture: !!photo,
      },
    };
  }

  /** Scroll to and focus the first field marked invalid, after errors are set. */
  function focusFirstError() {
    requestAnimationFrame(() => {
      const el = document.querySelector('[aria-invalid="true"]') as HTMLElement | null;
      if (el) { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); el.focus({ preventScroll: true }); }
    });
  }

  async function submit() {
    // Before saving, check each required field, HIGHLIGHT the ones that need
    // fixing, focus the first, and say so — rather than a Save button that is
    // just greyed out with no reason. Mirrors the server's own rules.
    if (!valid) {
      const errs: Record<string, string> = {};
      if (!walkIn && legalName.trim().length < 2) errs.legalName = 'Enter the registered / legal name.';
      if (shopName.trim().length < 2) errs['shops.0.shopName'] = 'Enter the shop / trading name.';
      if (!phoneOk) errs.phone = 'Ten digits, or leave it blank.';
      if (!pincodeOk) errs['shops.0.pincode'] = 'Six digits, or leave it blank.';
      if (!walkIn && address1.trim().length < 3) errs['shops.0.addressLine1'] = 'Enter the shop address.';
      if (!walkIn && !storeTypeId) errs['shops.0.storeTypeId'] = 'Choose a store type.';
      if (!walkIn && !beatId) errs['shops.0.beatId'] = 'Choose a beat.';
      setFieldErrors(errs);
      if (Object.keys(errs).length > 0) {
        toast.show('Please correct the highlighted field before saving.', 'danger');
        focusFirstError();
      } else if (!readAloud) {
        toast.show('Please read out and confirm the consent before saving.', 'danger');
      }
      return;
    }

    setBusy(true);
    setFieldErrors({});
    const payload = buildPayload();

    /**
     * Queued when offline — the whole point of doing this in the field.
     *
     * `kind: 'customer'` has been in the outbox type since it was written and
     * was never used, because no screen could create a customer. Spec 5.14
     * asks for exactly this: new customers and orders queue offline.
     */
    if (!navigator.onLine) {
      await outbox.enqueue({
        method: 'POST',
        path: '/api/customers/register',
        body: payload,
        label: `New shop — ${shopName.trim()}`,
        kind: 'customer',
      });
      toast.show(t('app.offlineHint'), 'ok');
      setBusy(false);
      onSaved();
      return;
    }

    try {
      /**
       * REPORT WHAT THE SERVER DID, NOT WHAT THIS SCREEN GUESSED.
       *
       * This used to announce "sent for approval" unconditionally, composed
       * before the request was even made and shown whatever came back. For an
       * owner_admin the server creates the customer ACTIVE and pushes it to
       * Business Central on the spot — so the app reported an approval that had
       * not happened, could not happen, and hid the one fact that mattered:
       * whether BC accepted the customer and what number it gave back.
       *
       * The response carries `customerCode` (BC's real number once the push
       * lands, the provisional CUST##### if it did not), `syncedToBc` and a
       * `message` written by the route that knows which path it took. All three
       * are shown. A push that failed is a warning, not a tick — a green tick
       * over a customer Business Central never received is how a retailer ends
       * up missing from the books with nobody looking for them.
       */
      const res = await api.post<{
        customerId: string;
        customerCode: string;
        provisionalCode?: string;
        syncedToBc: boolean;
        syncPending?: boolean;
        requiresApproval: boolean;
        message?: string;
        duplicate?: boolean;
      }>('/api/customers/register', payload);

      // Already saved (a double-tap or an offline replay of this same form). Say
      // so instead of a second "created" message, and close. #3.
      if (res.duplicate) {
        toast.show(`Already saved${res.customerCode ? ` as ${res.customerCode}` : ''}.`, 'ok');
        onSaved();
        return;
      }

      /**
       * The photo goes up AFTER the customer exists, because it is keyed to
       * the customer id and there is no id until this reply.
       *
       * A failure here is reported and nothing else: the shop is saved, and
       * the photo can be added from its page. Throwing would tell the
       * salesman the registration failed when it did not, and he would add
       * the shop a second time.
       */
      if (photo) {
        try {
          await postFile(`/api/customers/${res.customerId}/photo`, photo.blob, 'shop.jpg');
        } catch {
          toast.show('The shop was saved, but the photo did not upload. Add it from the shop page.', 'danger');
        }
      }

      const name = shopName.trim();
      if (walkIn) {
        toast.show(`Customer record saved — ${name} added as a walk-in.`, 'ok');
      } else if (res.requiresApproval) {
        toast.show(`Customer record saved — ${name} sent for approval; it reaches Business Central once approved.`, 'ok');
      } else if (res.syncedToBc) {
        toast.show(`Customer record saved — ${name} is in Business Central as ${res.customerCode}.`, 'ok');
      } else if (res.syncPending) {
        /**
         * Saved and orderable RIGHT NOW; Business Central was just unreachable
         * this second, so the sync is queued and runs on its own. This is the
         * ordinary case when BC is slow, not a failure — so it is a calm note,
         * not the red alarm it used to be, which made every salesman think the
         * shop had not saved and add it again.
         */
        toast.show(
          `Customer record saved — ${name} (${res.customerCode}). `
          + (res.message ?? 'It will sync to Business Central automatically.'),
          'neutral',
        );
      } else {
        /**
         * Not synced AND not merely queued — Business Central actively refused
         * it and a person has to act (see Settings → Sync). This one keeps the
         * red tone, because it will not fix itself.
         */
        toast.show(
          `${name} saved as ${res.customerCode}, but Business Central refused it. `
          + (res.message ?? 'Open Settings → Sync to resolve it.'),
          'danger',
        );
      }
      onSaved();
    } catch (e) {
      const err = e as { code?: string; fields?: Record<string, string>; message?: string };

      /**
       * THE SHOP IS ALREADY HERE — handled before the generic branch below.
       *
       * This is the reported bug. 373 customers came from Business Central
       * with their real mobile numbers, so the commonest thing a salesman
       * can do is add a shop that already exists. He used to get "An account
       * with this mobile number already exists": no name, no code, nothing
       * to tap, while standing in the shop.
       *
       * Falling through to the generic `err.fields` branch would be worse
       * than before — it would set field errors keyed `customerId` and
       * `legalName`, highlight nothing, and show no message at all.
       */
      if (err.code === 'CUSTOMER_EXISTS' && err.fields?.customerId) {
        setExisting({
          customerId: err.fields.customerId,
          customerCode: err.fields.customerCode ?? '',
          legalName: err.fields.legalName ?? '',
          shopName: err.fields.shopName ?? '',
          awaitingApproval: err.fields.awaitingApproval === 'yes',
          message: err.message ?? 'This shop is already on the system',
        });
      } else if (err.fields) {
        setFieldErrors(err.fields);
        toast.show('Please correct the highlighted field before saving.', 'danger');
        focusFirstError();
      } else toast.show(err.message ?? 'Could not add the customer', 'danger');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet
      open
      fullscreen
      title="Add a customer"
      onClose={onClose}
      footer={
        <>
          {/* A full-screen panel has no backdrop to tap, so Cancel is the only
              way out other than the back gesture — it has to be here. */}
          <Button variant="ghost" block onClick={onClose} disabled={busy}>
            {t('app.cancel')}
          </Button>
          {/* Always tappable (only blocked while saving) so a tap on an
              incomplete form gives a reason and focuses the field, instead of a
              silently-greyed button. submit() validates first. */}
          <Button block busy={busy} disabled={busy} onClick={() => void submit()}>
            {walkIn ? 'Add walk-in customer' : approvesOwnEntry ? 'Add new customer' : 'Send for approval'}
          </Button>
        </>
      }
    >
      {/*
        ONE PAGE, GROUPED. The same three sections as the edit form — who the
        customer is, where they are, how they are classified — then the
        consent and capture that only a new registration needs. City and
        pincode now sit in Address, matching what the edit form asks for.
      */}
      <div className="stack">
        <FormSection title="Customer">
          <Switch
            checked={walkIn}
            onChange={setWalkIn}
            label="Walk-in / one-time"
            hint="Fewer details. Can be converted to a full customer later, and sells the same way."
          />

          {!walkIn && (
            <TextInput
              label="Registered / legal name" required value={legalName}
              onChange={(e) => setLegalName(e.target.value)}
              error={fieldErrors.legalName}
              hint="As it should appear on the tax invoice"
            />
          )}
          <TextInput
            label={walkIn ? 'Shop name' : 'Shop / trading name'} required value={shopName}
            onChange={(e) => setShopName(e.target.value)}
            error={fieldErrors['shops.0.shopName']}
          />
          <TextInput
            label="Mobile number" value={phone} numeric
            onChange={(e) => { setPhone(phoneInputDigits(e.target.value)); setExisting(null); }}
            error={fieldErrors.phone
              ?? (digits.length > 0 && digits.length !== 10 ? 'Ten digits, or leave it blank' : undefined)}
            hint={digits.length === 0
              ? 'Optional. Without it the shop cannot sign in, and gets no invoice or '
                + 'payment reminder on WhatsApp.'
              : 'How they sign in and receive invoices. Ten digits.'}
          />

          {/*
            THE SHOP IS ALREADY ON THE SYSTEM.

            Shown in place of an error, because that is what this is. The
            salesman is standing in a real shop that the app already knows
            about; the only thing he got wrong was not finding it first.
          */}
          {existing && (
            <Banner tone="warn" title="This shop is already on the system">
              <div className="stack-2">
                <span className="t-sm">{existing.message}</span>
                {existing.customerCode && (
                  <div className="t-xs t-muted">
                    {existing.legalName}
                    {existing.customerCode ? ` · ${existing.customerCode}` : ''}
                  </div>
                )}
                {existing.awaitingApproval ? (
                  <div className="t-xs t-muted">
                    It was registered by somebody else and is waiting for the office to approve
                    it. Nothing more to do here.
                  </div>
                ) : (
                  <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                    <Button
                      size="sm"
                      onClick={() => { onClose(); navigate(`/customers/${existing.customerId}`); }}
                    >
                      Open the shop
                    </Button>
                  </div>
                )}
              </div>
            </Banner>
          )}

          <TextInput
            label="Contact person" value={pocName}
            onChange={(e) => setPocName(e.target.value)}
            error={fieldErrors.pocName}
            hint="Who to ask for. Goes to Business Central as the customer's contact."
          />

          {!walkIn && (
            <TextInput
              label="GSTIN" value={gstin}
              onChange={(e) => setGstin(e.target.value)}
              error={fieldErrors.gstin}
              hint="Leave blank for an unregistered shop — they get a bill of supply instead"
            />
          )}
        </FormSection>

        <FormSection title="Address">
          <TextInput
            label="Address" required={!walkIn} value={address1}
            onChange={(e) => setAddress1(e.target.value)}
            error={fieldErrors['shops.0.addressLine1']}
          />
          <TextInput
            label="City" value={city}
            onChange={(e) => setCity(e.target.value)}
            error={fieldErrors['shops.0.city']}
          />
          <TextInput
            label="Pincode" numeric maxLength={6} value={pincode}
            onChange={(e) => setPincode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            error={fieldErrors['shops.0.pincode']
              ?? (pincode.length > 0 && pincode.length !== 6 ? 'Six digits, or leave it blank' : undefined)}
          />

          {/*
            STATE. It decides place of supply, and therefore whether this
            shop's invoices carry CGST+SGST or IGST. Defaulted to the company's
            own state rather than left empty, because for this distributor
            almost every answer is Rajasthan and a required field with no
            sensible default is a field people learn to click past.
          */}
          {states.data && (
            <Select
              label="State"
              value={stateGstCode}
              onChange={(e) => setStateGstCode(e.target.value)}
              options={states.data.items.map((s) => ({
                value: s.gstCode, label: `${s.name} · ${s.gstCode}`,
              }))}
              hint="Where the goods are delivered. Decides CGST+SGST or IGST."
            />
          )}
        </FormSection>

        <FormSection title="Classification">
          {/*
            STORE TYPE AND BEAT — Business Central's Global Dimension 1 and 2.
            A shop registered without them reaches BC with no dimensions, and
            its sales cannot be broken down by outlet type or by round in BC's
            own reporting. Both optional: better an unset dimension than a
            guessed one.
          */}
          {storeTypes.data && storeTypes.data.items.length > 0 && (
            <Select
              label="Store type" value={storeTypeId}
              required={!walkIn}
              error={fieldErrors['shops.0.storeTypeId']}
              onChange={(e) => setStoreTypeId(e.target.value)}
              options={[
                { value: '', label: 'Choose store type' },
                ...storeTypes.data.items.map((s) => ({ value: s.id, label: s.name })),
              ]}
              hint="Kirana, tea stall, general store — how this outlet is reported."
            />
          )}

          {beats.data && beats.data.items.length > 0 && (
            <Select
              label="Beat" value={beatId}
              required={!walkIn}
              error={fieldErrors['shops.0.beatId']}
              onChange={(e) => setBeatId(e.target.value)}
              options={[
                { value: '', label: 'Choose beat' },
                ...beats.data.items.map((b) => ({
                  value: b.id,
                  label: b.area_name ? `${b.name} · ${b.area_name}` : b.name,
                })),
              ]}
              hint="Which round calls on this shop."
            />
          )}
        </FormSection>

        {/*
          LOCATION AND PHOTO — always shown, like the customer page. Both are
          optional and sent only if captured; the consent attestation below
          covers them. The pin takes a GPS fix or a pasted Google Maps link,
          and the photo is uploaded after the customer exists, because it is
          keyed to its id.
        */}
        <FormSection title="Location & photo">
          <Card className="stack-2">
            <span className="t-semi">Shop location</span>
            {coords ? (
              <div className="t-xs t-muted">
                Pin saved · accurate to about {Math.round(coords.accuracyM)} m
              </div>
            ) : locationLink.trim() ? (
              <div className="t-xs t-muted">Location link added — checked when the shop is saved.</div>
            ) : (
              <div className="t-xs t-muted">Helps plan the delivery round and find the shop.</div>
            )}
            <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
              <Button
                variant="quiet" size="sm" busy={locating}
                onClick={() => void captureLocation()}
              >
                {coords ? 'Update from here' : 'Capture location'}
              </Button>
              <Button variant="quiet" size="sm" onClick={() => setShowLink((v) => !v)}>
                Paste Google link
              </Button>
            </div>
            {showLink && (
              <TextInput
                label="Google link"
                placeholder="https://maps.app.goo.gl/…"
                value={locationLink}
                onChange={(e) => setLocationLink(e.target.value)}
                error={fieldErrors['shops.0.locationUrl']}
                hint="Paste the share link from the Google Maps app, or the full map URL — we'll pull the exact coordinates automatically."
              />
            )}
          </Card>

          <Card className="stack-2">
            <span className="t-semi">Shop photo</span>
            {/* Sized inline rather than with .thumb, which is 52px — too
                small to tell whether the shop front is actually in frame. */}
            {photoUrl
              ? (
                <img
                  src={photoUrl} alt="Shop front"
                  style={{
                    width: '100%', maxHeight: 180, objectFit: 'cover',
                    borderRadius: 'var(--radius)',
                  }}
                />
              )
              : <div className="t-xs t-muted">A picture of the shop front helps the driver find it.</div>}
            <Button variant="quiet" size="sm" onClick={() => setCamera(true)}>
              {photo ? 'Retake' : 'Take a photo of the shop'}
            </Button>
          </Card>
        </FormSection>

        {/*
          CONSENT — the last row. Only a new registration has it. The shopkeeper
          is the data subject; the person filling this in is not, so the
          attestation names what was actually done and is off by default. It
          also covers the location and photo captured above.
        */}
        <FormSection title="Consent">
          <Card className="stack-2">
            <Switch
              checked={readAloud}
              onChange={setReadAloud}
              label="I have explained the terms and privacy notice to the shopkeeper, and they agreed"
              hint="Covers using their details to take orders, raise invoices and keep their ledger,
                    saving the shop's location and photo, and sending their login code by WhatsApp.
                    Do not tick this unless you have actually told them."
            />
          </Card>
        </FormSection>

        {camera && (
          <CameraSheet
            open facing="environment" title="Shop front"
            compressOptions={{ maxDimension: 1600, maxBytes: 3 * 1024 * 1024 }}
            onCapture={(p) => { setPhoto(p); setCamera(false); }}
            onClose={() => setCamera(false)}
          />
        )}

        {fieldErrors.consents && (
          <Banner tone="danger" title="Consent is required">
            <span className="t-sm">{fieldErrors.consents}</span>
          </Banner>
        )}

        {!readAloud && (
          <div className="t-xs t-muted">
            The shopkeeper has to agree before their details can be saved.
          </div>
        )}
        {!walkIn && (
          <div className="t-xs t-muted">
            {approvesOwnEntry
              ? 'Saved active straight away and sent to Business Central, which assigns the '
                + 'customer number. No approval step — you are the approver.'
              : 'A full customer goes to the office for approval before they can order on credit. '
                + 'They reach Business Central once approved, not before.'}
          </div>
        )}
      </div>
    </Sheet>
  );
}
